import {
  AlertTriangle,
  Download,
  Plus,
  Upload,
  UserRoundSearch,
} from "lucide-react";
import ExcelJS from "exceljs";
import { useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useApp } from "../../domain/AppContext";
import { compareClassNames, formatClassCodesInText, formatClassName, parseClassCode } from "../../domain/classCode";
import type { Student } from "../../domain/types";
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  InlineMessage,
  PageHeader,
  SearchField,
  StatusChip,
} from "../../components/ui";
import { OlympiadAreaIcon } from "../../components/OlympiadAreaIcon";

export function StudentsPage() {
  const { profile, data, createStudent, notify } = useApp();
  const [query, setQuery] = useState("");
  const [className, setClassName] = useState("all");
  const [status, setStatus] = useState("all");
  const [importOpen, setImportOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importCandidates, setImportCandidates] = useState<typeof newStudent[]>([]);
  const [importIssues, setImportIssues] = useState<string[]>([]);
  const [importUnitId, setImportUnitId] = useState("");
  const [newStudent, setNewStudent] = useState({
    cpf: "",
    unitId: "",
    name: "",
    ra: "",
    className: "Sem turma",
    grade: "9º ano",
    email: "",
    guardian: "",
    birthDate: "",
    active: true,
    mustChangePassword: true,
  });
  if (!data) return null;
  const classes = [...new Set(data.students.map((item) => item.className))].sort(compareClassNames);
  const filtered = useMemo(
    () =>
      data.students.filter((student) => {
        const normalized = query.trim().toLocaleLowerCase("pt-BR");
        if (
          normalized &&
          !`${student.name} ${student.ra} ${student.email} ${student.className} ${formatClassName(student.className)}`
            .toLocaleLowerCase("pt-BR")
            .includes(normalized)
        )
          return false;
        if (className !== "all" && student.className !== className)
          return false;
        if (
          status === "incomplete" &&
          student.className !== "Sem turma" &&
          !student.mustChangePassword
        )
          return false;
        if (status === "active" && !student.active) return false;
        return true;
      }),
    [className, data.students, query, status],
  );
  const noClass = data.students.filter(
    (item) => item.className === "Sem turma",
  ).length;

  const downloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("consulta_3646", { views: [{ state: "frozen", ySplit: 1 }] });
    sheet.columns = [
      { header: "ALUNO_CPF", key: "cpf", width: 17 },
      { header: "ALUNO", key: "ra", width: 14 },
      { header: "TURMA_PREF", key: "className", width: 18 },
      { header: "NOME_ALUNO", key: "name", width: 38 },
      { header: "ALUNO_EMAIL", key: "email", width: 34 },
    ];
    sheet.addRow({ cpf: "00000000000", ra: "20240000", className: "9º C", name: "Nome completo do estudante", email: "estudante@escola.br" });
    sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
    sheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF173F7A" } };
    sheet.getRow(1).alignment = { vertical: "middle" };
    sheet.getRow(1).height = 24;
    sheet.autoFilter = "A1:E2";
    ["A", "B"].forEach((column) => { sheet.getColumn(column).numFmt = "@"; });
    const bytes = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "modelo-estudantes-oficina-sapiens.xlsx";
    anchor.click();
    URL.revokeObjectURL(url);
  };
  const deriveGrade = (className: string) => {
    const codedClass = parseClassCode(className);
    if (codedClass) return codedClass.grade;
    const fundamental = className.match(/^([6-9])\s*[ºo]/i)?.[1];
    if (fundamental) return `${fundamental}º ano`;
    const highSchool = className.match(/^([1-3])\s*[ªa]/i)?.[1];
    return highSchool ? `${highSchool}ª série EM` : "";
  };
  const parseStudentXlsx = async (file: File) => {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const sheet = workbook.worksheets[0];
    const candidates: typeof newStudent[] = [];
    const issues: string[] = [];
    if (!sheet) return notify({ type: 'error', title: 'Planilha sem aba de dados' });
    const headers = sheet.getRow(1).values as unknown[];
    const expected = ["ALUNO_CPF", "ALUNO", "TURMA_PREF", "NOME_ALUNO", "ALUNO_EMAIL"];
    if (expected.some((header, index) => String(headers[index + 1] ?? '').trim().toUpperCase() !== header)) return notify({ type: 'error', title: 'Colunas incompatíveis', description: `Use o modelo Excel com as colunas ${expected.join(', ')}.` });
    sheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return;
      const cpf = row.getCell(1).text.replace(/\D/g, '');
      const ra = row.getCell(2).text.replace(/\D/g, '');
      const candidateClass = row.getCell(3).text.trim();
      const name = row.getCell(4).text.trim();
      const email = row.getCell(5).text.trim().toLowerCase();
      const grade = deriveGrade(candidateClass);
      if (!cpf && !ra && !candidateClass && !name && !email) return;
      if (!/^\d{11}$/.test(cpf) || !/^\d{8}$/.test(ra) || !name || !candidateClass || !grade || (email && !/^\S+@\S+\.\S+$/.test(email))) { issues.push(`Linha ${rowNumber}: dados obrigatórios inválidos`); return; }
      if (data.students.some((item) => item.ra === ra)) { issues.push(`Linha ${rowNumber}: RA ${ra} já cadastrado e preservado`); return; }
      if (candidates.some((item) => item.ra === ra)) { issues.push(`Linha ${rowNumber}: RA ${ra} duplicado no arquivo`); return; }
      candidates.push({ cpf, unitId: '', name, ra, className: candidateClass, grade, email, guardian: '', birthDate: '', active: true, mustChangePassword: true });
    });
    setImportCandidates(candidates); setImportIssues(issues);
    notify({ type: candidates.length ? 'success' : 'error', title: `${candidates.length} novo(s) estudante(s) validado(s)`, description: issues.length ? `${issues.length} linha(s) foram bloqueadas.` : 'Nenhuma divergência encontrada.' });
  };
  const importStudents = async () => {
    if (!importUnitId) return notify({ type: 'error', title: 'Selecione a unidade dos estudantes' });
    setCreating(true);
    for (const candidate of importCandidates) {
      const result = await createStudent({ ...candidate, unitId: importUnitId });
      if (!result.ok) { setCreating(false); return notify({ type: 'error', title: 'Importação interrompida', description: result.error.message }); }
    }
    setCreating(false); setImportCandidates([]); setImportIssues([]); setImportUnitId(""); setImportOpen(false);
    notify({ type: 'success', title: 'Estudantes importados', description: 'Novos perfis exigirão troca de senha no primeiro acesso.' });
  };

  const submitStudent = async (event: React.FormEvent) => {
    event.preventDefault();
    setCreating(true);
    const result = await createStudent(newStudent);
    setCreating(false);
    if (result.ok) {
      setNewOpen(false);
      setNewStudent({ cpf: "", unitId: "", name: "", ra: "", className: "Sem turma", grade: "9º ano", email: "", guardian: "", birthDate: "", active: true, mustChangePassword: true });
      notify({ type: "success", title: "Estudante cadastrado", description: "A conta foi criada e o primeiro acesso exigirá a troca da senha inicial." });
    } else notify({ type: "error", title: result.error.message });
  };

  return (
    <div className="students-page">
      <PageHeader
        eyebrow="Pessoas"
        title="Estudantes"
        description={`${data.students.length} cadastros`}
        actions={profile?.isSystemManager ?
          <>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload size={17} /> Importar estudantes
            </Button>
            <Button onClick={() => setNewOpen(true)}>
              <Plus size={17} /> Novo estudante
            </Button>
          </>
        : undefined}
      />
      <div className="student-kpi-row">
        <article>
          <strong>{data.students.length}</strong>
          <span>Estudantes cadastrados</span>
        </article>
        <button
          type="button"
          className={noClass ? "attention" : ""}
          onClick={() => {
            setClassName("Sem turma");
            setStatus("all");
          }}
        >
          <strong>{noClass}</strong>
          <span>Sem turma associada</span>
        </button>
        <article>
          <strong>
            {data.students.filter((item) => item.lastAccess).length}
          </strong>
          <span>Já acessaram</span>
        </article>
        <article>
          <strong>{data.enrollments.length}</strong>
          <span>Inscrições</span>
        </article>
      </div>
      <div className="admin-filter-bar">
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Buscar por nome, RA ou e-mail..."
        />
        <select
          value={className}
          onChange={(event) => setClassName(event.target.value)}
        >
          <option value="all">Todas as turmas</option>
          {classes.map((item) => (
            <option key={item} value={item}>{formatClassName(item)}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">Todos os cadastros</option>
          <option value="active">Ativos</option>
          <option value="incomplete">Incompletos</option>
        </select>
      </div>
      {filtered.length ? (
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>Estudante</th>
                <th>RA</th>
                <th>Turma</th>
                <th>E-mail</th>
                <th>Inscrições</th>
                <th>Último acesso</th>
                <th>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((student) => (
                <tr key={student.id}>
                  <td data-label="Estudante">
                    <div className="person-cell">
                      <span className="avatar">{student.initials}</span>
                      <strong>{student.name}</strong>
                    </div>
                  </td>
                  <td data-label="RA">
                    <code>{student.ra}</code>
                  </td>
                  <td data-label="Turma">
                    {student.className === "Sem turma" ? (
                      <span className="missing-value">
                        <AlertTriangle size={14} /> Sem turma
                      </span>
                    ) : (
                      formatClassName(student.className)
                    )}
                  </td>
                  <td data-label="E-mail">{student.email}</td>
                  <td data-label="Inscrições">
                    {
                      data.enrollments.filter(
                        (item) =>
                          item.studentIds.includes(student.id) &&
                          item.status !== "cancelled",
                      ).length
                    }
                  </td>
                  <td data-label="Último acesso">
                    {student.lastAccess ?? "Nunca acessou"}
                  </td>
                  <td>
                    <Link
                      className="button button-ghost"
                      to={`/estudantes/${student.id}`}
                    >
                      Ver perfil
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={<UserRoundSearch />}
          title="Nenhum estudante encontrado"
          description="Ajuste a busca ou os filtros."
        />
      )}
      <Dialog
        open={newOpen}
        title="Cadastrar estudante"
        description="O RA será o identificador único e o primeiro acesso exigirá troca de senha."
        onClose={() => setNewOpen(false)}
        actions={
          <>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Cancelar</Button>
            <Button type="submit" form="new-student-form" loading={creating}>Cadastrar estudante</Button>
          </>
        }
      >
        <form id="new-student-form" className="dialog-form" onSubmit={submitStudent}>
          <Field label="Nome completo"><input required value={newStudent.name} onChange={(event) => setNewStudent({ ...newStudent, name: event.target.value })} /></Field>
          <div className="form-grid two">
            <Field label="CPF"><input required inputMode="numeric" value={newStudent.cpf} onChange={(event) => setNewStudent({ ...newStudent, cpf: event.target.value.replace(/\D/g, "").slice(0, 11) })} /></Field>
            <Field label="RA"><input required inputMode="numeric" value={newStudent.ra} onChange={(event) => setNewStudent({ ...newStudent, ra: event.target.value.replace(/\D/g, "").slice(0, 8) })} /></Field>
            <Field label="Unidade"><select required value={newStudent.unitId} onChange={(event) => setNewStudent({ ...newStudent, unitId: event.target.value })}><option value="">Selecione</option>{data.institution.units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}</select></Field>
            <Field label="Turma"><select value={newStudent.className} onChange={(event) => setNewStudent({ ...newStudent, className: event.target.value })}><option>Sem turma</option>{classes.filter((item) => item !== "Sem turma").map((item) => <option key={item} value={item}>{formatClassName(item)}</option>)}</select></Field>
            <Field label="Série"><select value={newStudent.grade} onChange={(event) => setNewStudent({ ...newStudent, grade: event.target.value })}><option>6º ano</option><option>7º ano</option><option>8º ano</option><option>9º ano</option><option>1ª série EM</option></select></Field>
            <Field label="E-mail"><input type="email" value={newStudent.email} onChange={(event) => setNewStudent({ ...newStudent, email: event.target.value })} /></Field>
            <Field label="Responsável"><input value={newStudent.guardian} onChange={(event) => setNewStudent({ ...newStudent, guardian: event.target.value })} /></Field>
            <Field label="Nascimento"><input type="date" value={newStudent.birthDate} onChange={(event) => setNewStudent({ ...newStudent, birthDate: event.target.value })} /></Field>
          </div>
          <InlineMessage title="Credencial inicial protegida">A conta é criada automaticamente, já ativa, com a senha inicial definida com segurança.</InlineMessage>
        </form>
      </Dialog>
      <Dialog
        open={importOpen}
        title="Importar estudantes"
        description="Valide o Excel por CPF e RA antes de qualquer escrita."
        onClose={() => setImportOpen(false)}
        size="large"
      >
        <div className="import-stepper">
          <span className="done">1 Enviar arquivo</span>
          <span>2 Mapear colunas</span>
          <span>3 Validar dados</span>
          <span>4 Revisar erros</span>
          <span>5 Confirmar</span>
        </div>
        <div className="upload-zone">
          <Upload size={28} />
          <strong>Selecione uma planilha Excel</strong>
          <p>Use o modelo baseado na planilha de referência institucional.</p>
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => { const file = event.target.files?.[0]; if (file) void parseStudentXlsx(file); }}
          />
        </div>
        <Field label="Unidade dos estudantes"><select required value={importUnitId} onChange={(event) => setImportUnitId(event.target.value)}><option value="">Selecione antes de importar</option>{data.institution.units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}</select></Field>
        <InlineMessage title="Nada é importado às cegas">
          Registros existentes são reconhecidos pelo RA e preservados. Este fluxo cria somente novos cadastros validados.
        </InlineMessage>
        {importCandidates.length ? <div className="import-preview"><Upload size={20} /><div><strong>{importCandidates.length} novo(s) cadastro(s) pronto(s)</strong><p>{importIssues.length ? `${importIssues.length} linha(s) bloqueadas e não serão importadas.` : 'Todas as linhas foram validadas.'}</p></div></div> : null}
        {importIssues.length ? <ul className="import-issues">{importIssues.slice(0, 8).map((item) => <li key={item}>{item}</li>)}</ul> : null}
        <div className="dialog-actions inline">
          <Button variant="secondary" onClick={() => void downloadTemplate()}>
            <Download size={17} /> Baixar modelo
          </Button>
          <Button loading={creating} disabled={!importCandidates.length || !importUnitId} onClick={() => void importStudents()}>
            Importar {importCandidates.length || ''} estudante(s)
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

export function StudentDetailPage() {
  const { id } = useParams();
  const { profile, data, updateStudent, setStudentActive, resetStudentPassword, endUserSessions, notify } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState<
    "overview" | "olympiads" | "history" | "data" | "security"
  >("overview");
  const student = data?.students.find((item) => item.id === id);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Student | null>(
    student ? structuredClone(student) : null,
  );
  const [resetOpen, setResetOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [working, setWorking] = useState(false);
  if (!data) return null;
  if (!student || !form) return <Navigate to="/estudantes" replace />;
  const enrollments = data.enrollments.filter(
    (item) =>
      item.studentIds.includes(student.id) && item.status !== "cancelled",
  );

  const save = async () => {
    setWorking(true);
    const result = await updateStudent(form);
    setWorking(false);
    if (result.ok) {
      setEditing(false);
      notify({ type: "success", title: "Dados do estudante atualizados" });
    } else notify({ type: "error", title: result.error.message });
  };

  const reset = async () => {
    setWorking(true);
    const result = await resetStudentPassword(student.id);
    setWorking(false);
    if (result.ok) setTemporaryPassword(result.data.temporaryPassword);
    else notify({ type: "error", title: result.error.message });
  };

  const toggleAccess = async () => {
    setWorking(true);
    const result = await setStudentActive(student.id, !student.active);
    setWorking(false);
    notify(result.ok ? { type: "success", title: student.active ? "Acesso do estudante bloqueado" : "Acesso do estudante reativado" } : { type: "error", title: result.error.message });
  };

  return (
    <div className="student-detail-page">
      <button
        className="back-link"
        type="button"
        onClick={() => navigate("/estudantes")}
      >
        ← Voltar para estudantes
      </button>
      <section className="student-profile-header">
        <span className="avatar large">{student.initials}</span>
        <div>
          <p className="eyebrow">Perfil do estudante</p>
          <h1>{student.name}</h1>
          <p>
            RA {student.ra} · {formatClassName(student.className)} · unidade {data.institution.units.find((unit) => unit.id === student.unitId)?.name ?? student.unitId ?? "não definida"}
          </p>
        </div>
        <StatusChip status={student.active ? "active" : "inactive"} />
        {profile?.isSystemManager ? <Button
          variant="secondary"
          onClick={() => setEditing((value) => !value)}
        >
          {editing ? "Cancelar edição" : "Editar dados"}
        </Button> : null}
      </section>
      <nav className="detail-tabs">
        {(
          ["overview", "olympiads", "history", "data", "security"] as const
        ).map((item) => (
          <button
            key={item}
            type="button"
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {item === "overview"
              ? "Visão geral"
              : item === "olympiads"
                ? "Olimpíadas"
                : item === "history"
                  ? "Histórico"
                  : item === "data"
                    ? "Dados"
                    : "Segurança"}
          </button>
        ))}
      </nav>
      <div className="student-tab-content">
        {editing || tab === "data" ? (
          <section className="profile-form">
            <h2>Dados cadastrais</h2>
            <div className="form-grid two">
              <Field label="Nome completo">
                <input
                  value={form.name}
                  disabled={!editing}
                  onChange={(event) =>
                    setForm({ ...form, name: event.target.value })
                  }
                />
              </Field>
              <Field label="RA">
                <input
                  value={form.ra}
                  disabled={!editing}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      ra: event.target.value.replace(/\D/g, "").slice(0, 8),
                    })
                  }
                />
              </Field>
              <Field label="Turma">
                <select
                  value={form.className}
                  disabled={!editing}
                  onChange={(event) =>
                    setForm({ ...form, className: event.target.value })
                  }
                >
                  {[
                    ...new Set(data.students.map((item) => item.className)),
                  ].sort(compareClassNames).map((item) => (
                    <option key={item} value={item}>{formatClassName(item)}</option>
                  ))}
                </select>
              </Field>
              <Field label="E-mail">
                <input
                  type="email"
                  value={form.email}
                  disabled={!editing}
                  onChange={(event) =>
                    setForm({ ...form, email: event.target.value })
                  }
                />
              </Field>
              <Field label="Data de nascimento">
                <input
                  type="date"
                  value={form.birthDate}
                  disabled={!editing}
                  onChange={(event) =>
                    setForm({ ...form, birthDate: event.target.value })
                  }
                />
              </Field>
              <Field label="Responsável">
                <input
                  value={form.guardian}
                  disabled={!editing}
                  onChange={(event) =>
                    setForm({ ...form, guardian: event.target.value })
                  }
                />
              </Field>
            </div>
            {editing ? (
              <Button loading={working} onClick={save}>
                Salvar alterações
              </Button>
            ) : null}
          </section>
        ) : null}
        {tab === "overview" && !editing ? (
          <div className="student-overview">
            <section>
              <h2>Olimpíadas atuais</h2>
              {enrollments.map((enrollment) => {
                const olympiad = data.olympiads.find(
                  (item) => item.id === enrollment.olympiadId,
                );
                return (
                  <article key={enrollment.id}>
                    {olympiad ? <OlympiadAreaIcon olympiad={olympiad} /> : null}
                    <div>
                      <strong>{olympiad?.acronym} 2026</strong>
                      <p>{olympiad?.name}</p>
                    </div>
                    <StatusChip status={enrollment.status} />
                  </article>
                );
              })}
            </section>
            <section>
              <h2>Últimas atividades</h2>
              {data.activities
                .filter((item) => item.actor === student.name && !`${item.module} ${item.action}`.toLocaleLowerCase('pt-BR').includes('material'))
                .map((item) => (
                  <article key={item.id}>
                    <span />
                    <div>
                      <strong>{formatClassCodesInText(item.action)}</strong>
                      <p>{item.occurredAt}</p>
                    </div>
                  </article>
                ))}
            </section>
          </div>
        ) : null}
        {tab === "olympiads" ? (
          <section>
            <h2>Participações</h2>
            <div className="responsive-table">
              <table>
                <thead>
                  <tr>
                    <th>Olimpíada</th>
                    <th>Protocolo</th>
                    <th>Situação</th>
                    <th>Solicitação</th>
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((enrollment) => {
                    const olympiad = data.olympiads.find(
                      (item) => item.id === enrollment.olympiadId,
                    );
                    return (
                      <tr key={enrollment.id}>
                        <td>{olympiad?.name}</td>
                        <td>{enrollment.protocol}</td>
                        <td>
                          <StatusChip status={enrollment.status} />
                        </td>
                        <td>
                          {new Date(enrollment.requestedAt).toLocaleDateString(
                            "pt-BR",
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}
        {tab === "history" ? (
          <section>
            <h2>Histórico do estudante</h2>
            {data.activities
              .filter(
                (item) =>
                  (item.actor === student.name || item.action.includes("estudante")) &&
                  !`${item.module} ${item.action}`.toLocaleLowerCase('pt-BR').includes('material'),
              )
              .map((item) => (
                <article className="history-row" key={item.id}>
                  <span />
                  <div>
                    <strong>{formatClassCodesInText(item.action)}</strong>
                    <p>
                      {item.occurredAt} · {item.module}
                    </p>
                  </div>
                </article>
              ))}
          </section>
        ) : null}
        {tab === "security" ? (
          <section className="security-panel">
            <h2>Segurança da conta</h2>
            <div className="detail-data-grid">
              <div>
                <span>Último acesso</span>
                <strong>{student.lastAccess ?? "Nunca acessou"}</strong>
              </div>
              <div>
                <span>Sessões ativas</span>
                <strong>1 dispositivo</strong>
              </div>
              <div>
                <span>Troca obrigatória</span>
                <strong>{student.mustChangePassword ? "Sim" : "Não"}</strong>
              </div>
            </div>
            <InlineMessage title="Nenhuma senha antiga é exibida">
              A redefinição gera um valor temporário de exibição única e
              registra a operação.
            </InlineMessage>
            {profile?.isSystemManager ? <div className="security-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  setResetOpen(true);
                  setTemporaryPassword("");
                }}
              >
                Redefinir senha
              </Button>
              <Button
                variant="secondary"
                onClick={() => void endUserSessions(student.id).then((result) => notify(result.ok ? { type: "success", title: "Sessões encerradas" } : { type: "error", title: "Sessões não encerradas", description: result.error.message }))}
              >
                Encerrar todas as sessões
              </Button>
              <Button
                variant={student.active ? "danger" : "secondary"}
                loading={working}
                onClick={toggleAccess}
              >
                {student.active ? "Bloquear acesso" : "Reativar acesso"}
              </Button>
            </div> : <InlineMessage title="Acesso protegido">Somente o Gestor de Sistema pode redefinir senhas, bloquear ou reativar usuários.</InlineMessage>}
          </section>
        ) : null}
      </div>
      <Dialog
        open={resetOpen}
        title={`Redefinir senha de ${student.name}?`}
        description="A senha atual deixará de funcionar e a troca será obrigatória no próximo acesso."
        onClose={() => setResetOpen(false)}
        closeable={!working}
        actions={
          !temporaryPassword ? (
            <>
              <Button variant="ghost" onClick={() => setResetOpen(false)}>
                Cancelar
              </Button>
              <Button loading={working} onClick={reset}>
                Gerar senha temporária
              </Button>
            </>
          ) : (
            <Button onClick={() => setResetOpen(false)}>Concluir</Button>
          )
        }
      >
        {temporaryPassword ? (
          <div className="temporary-password">
            <InlineMessage type="success" title="Senha temporária gerada">
              Ela será exibida uma única vez neste fluxo local.
            </InlineMessage>
            <code>{temporaryPassword}</code>
            <Button
              variant="secondary"
              onClick={async () => {
                await navigator.clipboard.writeText(temporaryPassword);
                notify({ type: "success", title: "Senha temporária copiada" });
              }}
            >
              Copiar
            </Button>
            <p>Expira em 24 horas · troca obrigatória no login</p>
          </div>
        ) : (
          <div className="reset-options">
            <label>
              <input type="radio" name="reset-mode" defaultChecked /> Gerar
              senha temporária
            </label>
            <label>
              <input type="checkbox" defaultChecked /> Exigir alteração no
              próximo login
            </label>
          </div>
        )}
      </Dialog>
    </div>
  );
}
